import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/4412921/pexels-photo-4412921.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      toast.success("Willkommen!");
      navigate("/rechnungen");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="h-9 w-9 bg-primary flex items-center justify-center">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-extrabold text-2xl tracking-tight">FAKTURA</span>
          </div>

          <h1 className="font-heading text-4xl font-extrabold tracking-tight mb-2">
            {mode === "login" ? "Anmelden" : "Konto erstellen"}
          </h1>
          <p className="text-slate-500 text-sm mb-8">
            Deine Rechnungen. Bausteine, Live-Vorschau, PDF & DOCX.
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="register-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Max Mustermann"
                  className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@beispiel.de"
                className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="rounded-none border-slate-300 focus-visible:ring-0 focus-visible:border-primary"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full rounded-none bg-primary hover:bg-[#00227A] h-11 text-white"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {mode === "login" ? "Anmelden" : "Registrieren"}
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6">
            {mode === "login" ? "Noch kein Konto? " : "Bereits registriert? "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-primary font-medium hover:underline"
            >
              {mode === "login" ? "Jetzt registrieren" : "Anmelden"}
            </button>
          </p>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative">
        <img src={BG} alt="Büro" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/20 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/10" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-3xl font-extrabold tracking-tight leading-tight">
            Rechnungen schreiben,<br />wie am Reißbrett.
          </p>
          <p className="text-white/80 mt-3 text-sm max-w-md">
            Definiere links – sieh das fertige Dokument live rechts. Speichere als PDF & Word.
          </p>
        </div>
      </div>
    </div>
  );
}
